import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  useBreakpointValue,
  useColorModeValue,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Skeleton,
  AspectRatio,
  Tag,
  Badge,
  Divider,
  Center,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { animated, useSpring } from "@react-spring/web";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import {
  SearchIcon,
  ViewIcon,
  StarIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./myswiper.css";
import { Link } from "react-router-dom";

const Category = ({ title, data, subcategory = [], isEven = false, index }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [loadedImages, setLoadedImages] = useState({});
  const [activeFilter, setActiveFilter] = useState("all");
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: true, md: true, lg: false });

  // Prima chiamiamo tutti gli hooks incondizionatamente
  const evenBg = useColorModeValue(
    "rgba(248, 250, 252, 0.8)",
    "rgba(248, 250, 252, 0.2)",
  );
  const oddBg = useColorModeValue("transparent", "transparent");
  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.09)",
    "rgba(255, 255, 255, 0.08)",
  );
  const textColor = useColorModeValue("gray.100", "gray.200");
  const secondaryTextColor = useColorModeValue("gray.300", "gray.400");
  const darkText = useColorModeValue("gray.600", "gray.400");

  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.1)",
    "rgba(255, 255, 255, 0.1)",
  );
  const accentColor = useColorModeValue("orange.400", "orange.300");

  // Poi usiamo la logica condizionale sui valori
  const sectionBg = isEven ? evenBg : oddBg;
  const sectionTextColor = isEven ? darkText : textColor;

  const springs = useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    config: { tension: 280, friction: 60 },
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Flatten data function
  const flattenData = (obj) => {
    const result = [];
    const recurse = (currentObj, path = []) => {
      for (const [key, value] of Object.entries(currentObj)) {
        let currentPath =
          key !== "0" &&
          key !== "Teste mobili" &&
          key !== "Proiettori" &&
          key !== "Batteria" &&
          key !== "Console & controlli dmx" &&
          key !== "Teatrali" &&
          key !== "Architetturali"
            ? [...path, key]
            : [...path];

        if (typeof value === "object" && value !== null) {
          const isPrimitiveObject = Object.values(value).every(
            (v) => typeof v !== "object",
          );

          if (isPrimitiveObject) {
            const title = currentPath.join(" ");
            const text = Object.entries(value)
              .filter(([subKey]) => subKey !== "Image")
              .map(([subKey, subValue]) => `${subKey}: ${subValue}`)
              .join(" | ");
            const imageEntry = Object.entries(value).find(
              ([subKey]) => subKey === "Image",
            );
            const image = imageEntry ? imageEntry[1] : null;
            const category = currentPath[0] || title;

            if (image) {
              result.push({ title, text, image, category });
            } else {
              result.push({ title, text, category });
            }
          } else {
            recurse(value, currentPath);
          }
        }
      }
    };
    recurse(obj);
    return result;
  };

  // Flatten data once using useMemo
  const flattenedData = useMemo(() => flattenData(data), [data]);

  // Filtered data based on search term and subcategory
  const filteredData = useMemo(() => {
    let filtered = flattenedData;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.text.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply subcategory filter
    if (selectedSubcategory) {
      filtered = filtered.filter(
        (item) =>
          item.title
            .toLowerCase()
            .includes(selectedSubcategory.toLowerCase()) ||
          item.text.toLowerCase().includes(selectedSubcategory.toLowerCase()),
      );
    }

    // Apply active filter
    if (activeFilter === "with_images") {
      filtered = filtered.filter((item) => item.image);
    } else if (activeFilter === "newest") {
      filtered = filtered.slice(0, 10);
    }

    return filtered;
  }, [flattenedData, searchTerm, selectedSubcategory, activeFilter]);

  const handleImageLoad = (key) => {
    setLoadedImages((prev) => ({
      ...prev,
      [key]: true,
    }));
  };

  const openItemModal = (item) => {
    setSelectedItem(item);
    onOpen();
  };

  // Swiper configuration
  const getSlidesPerView = () => {
    if (windowWidth < 768) return 1;
    if (windowWidth < 1024) return 2;
    return 3;
  };

  const swiperConfig = {
    modules: [Navigation, Pagination, Autoplay],
    spaceBetween: 20,
    slidesPerView: getSlidesPerView(),
    navigation: !isTablet && true,
    pagination: {
      clickable: true,
      dynamicBullets: true,
    },
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    loop: filteredData.length > 3,
    breakpoints: {
      320: {
        slidesPerView: 1,
        spaceBetween: 10,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 15,
      },
      1024: {
        slidesPerView: 3,
        spaceBetween: 20,
      },
    },
  };

  const imageContext = require.context(
    "../../assets/images/materiali/",
    false,
    /\.(jpeg|jpg|png)$/,
  );

  return (
    <animated.div id="materialCarousels" style={{ ...springs }}>
      {/* Category Section - Full Width */}
      <Box
        bg={sectionBg}
        py={{ base: "4em", lg: "4em" }}
        px={{ base: 0, lg: 0 }}
        borderBottom="1px solid"
        borderColor={borderColor}
        position="relative"
      >
        <Container
          maxW="container.xl"
          py={{ lg: "3em" }}
          px={{ base: 4, md: 8, lg: 0 }}
        >
          {/* Category Header */}
          <VStack spacing={6} align="stretch" mb={{ base: 8, lg: 12 }}>
            <VStack spacing={4} align="center" textAlign="center">
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                color={sectionTextColor}
                lineHeight="1.2"
              >
                {title}
              </Heading>

              <Text
                color={sectionTextColor}
                fontSize={{ base: "md", md: "lg" }}
                maxW="800px"
                textAlign="center"
              >
                {filteredData.length > 0 ? (
                  <>
                    Esplora i nostri{" "}
                    <Text textColor={sectionTextColor} as={"strong"}>
                      {filteredData.length}
                    </Text>{" "}
                    prodotti in questa categoria
                  </>
                ) : (
                  "Nessun prodotto trovato con i filtri selezionati"
                )}
              </Text>
            </VStack>

            {/* Search and Filter Bar */}
            <Box
              bg="rgba(0, 0, 0, 0.8)"
              backdropFilter="blur(10px)"
              borderRadius="xl"
              p={{ base: 4, md: 6 }}
              mx={{ lg: "1.5em" }}
              border="1px solid"
              borderColor={borderColor}
            >
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr auto" }}
                gap={{ base: 4, md: 6 }}
                alignItems="end"
              >
                <GridItem>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color={accentColor} />
                    </InputLeftElement>
                    <Input
                      placeholder={`Cerca in ${title}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      borderColor={borderColor}
                      bg="rgba(0, 0, 0, 0.8)"
                      color={"grey.100"}
                      _hover={{ borderColor: accentColor }}
                      _focus={{
                        borderColor: accentColor,
                        boxShadow: `0 0 0 1px ${accentColor}`,
                        bg: "rgba(0, 0, 0, 0.3)",
                      }}
                      _placeholder={{ color: "gray.200" }}
                    />
                  </InputGroup>
                </GridItem>

                <GridItem>
                  <Select
                    value={selectedSubcategory}
                    onChange={(e) => setSelectedSubcategory(e.target.value)}
                    size="lg"
                    borderColor={borderColor}
                    bg="rgba(0, 0, 0, 0.8)"
                    color={"grey.100"}
                    _hover={{ borderColor: accentColor }}
                    _focus={{
                      borderColor: accentColor,
                      boxShadow: `0 0 0 1px ${accentColor}`,
                      bg: "rgba(0, 0, 0, 0.3)",
                    }}
                    placeholder="Tutti i tipi"
                  >
                    {subcategory.map((item, idx) => (
                      <option
                        key={idx}
                        value={item}
                        style={{ backgroundColor: "#1a202c", color: "#e2e8f0" }}
                      >
                        {item}
                      </option>
                    ))}
                  </Select>
                </GridItem>
              </Grid>
            </Box>
          </VStack>

          {/* Products Carousel */}
          {filteredData.length > 0 ? (
            <Box position="relative">
              <Swiper
                {...swiperConfig}
                style={{
                  position: "static",
                  width: "100%",
                  paddingBottom: "2.5em",
                  // margin: "0 2em",
                }}
              >
                {filteredData.map((item, idx) => (
                  <SwiperSlide key={idx}>
                    <Box
                      mx={{ lg: "1.35em" }}
                      bg={cardBg}
                      backdropFilter="blur(10px)"
                      borderRadius="md"
                      overflow="hidden"
                      border="1px solid"
                      borderColor={borderColor}
                      _hover={{
                        transform: "translateY(-8px)",
                        borderColor: accentColor,
                        boxShadow: `0 20px 40px rgba(255, 140, 0, 0.1)`,
                      }}
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      cursor="pointer"
                      onClick={() => openItemModal(item)}
                      height="100%"
                      position="relative"
                    >
                      {/* Product Image */}
                      <AspectRatio ratio={16 / 9}>
                        {item.image ? (
                          <Box>
                            <Image
                              src={imageContext(`./${item.image}`)}
                              alt={item.title}
                              onLoad={() => handleImageLoad(`${title}-${idx}`)}
                              loading="lazy"
                              objectFit={"contain"}
                              height={"100%"}
                              transition="transform 0.3s"
                              _hover={{ transform: "scale(1.05)" }}
                            />
                          </Box>
                        ) : (
                          <Center h="100%" bg="rgba(0, 0, 0, 0.8)">
                            <VStack spacing={3} mb={{ base: "4em" }}>
                              <ViewIcon boxSize={10} color="gray.400" />
                              <Text
                                color="gray.400"
                                fontSize="sm"
                                textAlign="center"
                              >
                                Immagine non disponibile
                              </Text>
                            </VStack>
                          </Center>
                        )}
                      </AspectRatio>

                      {/* Product Info Overlay */}
                      <Box
                        position="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        bg="linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)"
                        p={{ base: 4, md: 6 }}
                        transform="translateY(0)"
                        transition="transform 0.3s"
                      >
                        <VStack spacing={2} align="start">
                          {item.title !== item.category && (
                            <Heading
                              as="h3"
                              size="sm"
                              color="white"
                              noOfLines={1}
                            >
                              {item.title}
                            </Heading>
                          )}
                          <HStack spacing={2}>
                            <Badge colorScheme="orange" fontSize="2xs">
                              {/* {console.log(item.category)} */}
                              {item.category.endsWith("- ")
                                ? item.category.slice(0, -2)
                                : item.category}
                            </Badge>
                          </HStack>
                          <Text color="gray.100" fontSize="s" noOfLines={2}>
                            {item.text}
                          </Text>
                        </VStack>
                      </Box>

                      {/* Quick View Button */}
                      <Box
                        position="absolute"
                        top={4}
                        right={4}
                        opacity={0}
                        transition="opacity 0.3s"
                        _hover={{ opacity: 1 }}
                      >
                        <Button
                          size="xs"
                          colorScheme="orange"
                          leftIcon={<ViewIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openItemModal(item);
                          }}
                        >
                          Dettagli
                        </Button>
                      </Box>
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
          ) : (
            <Center py={12} bg="rgba(0, 0, 0, 0.3)" borderRadius="lg">
              <VStack spacing={4}>
                <SearchIcon boxSize={12} color="gray.500" />
                <Text color={sectionTextColor} fontSize="lg">
                  Nessun prodotto trovato per "{searchTerm}"
                </Text>
                <Button
                  colorScheme="orange"
                  variant="ghost"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedSubcategory("");
                    setActiveFilter("all");
                  }}
                  _hover={{ bg: "rgba(255, 140, 0, 0.1)" }}
                >
                  Ripristina filtri
                </Button>
              </VStack>
            </Center>
          )}
        </Container>
      </Box>

      {/* Product Detail Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(4px)" />
        <ModalContent
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          maxW={{ base: "95vw", md: "900px" }}
        >
          <ModalCloseButton
            bg="rgba(0, 0, 0, 0.5)"
            color="white"
            _hover={{ bg: "rgba(0, 0, 0, 0.7)" }}
            size="lg"
            borderRadius="full"
            m={4}
          />
          <ModalBody p={0}>
            {selectedItem && (
              <VStack spacing={0} align="stretch">
                {/* Product Image */}
                <Box bg="rgba(0, 0, 0, 0.3)" p={8}>
                  <Center>
                    {selectedItem.image ? (
                      <Image
                        src={imageContext(`./${selectedItem.image}`)}
                        alt={selectedItem.title}
                        maxH="400px"
                        maxW="100%"
                        objectFit="contain"
                        borderRadius="lg"
                      />
                    ) : (
                      <Center h="200px" w="100%">
                        <VStack spacing={4}>
                          <ViewIcon boxSize={12} color="gray.500" />
                          <Text color="gray.400">Immagine non disponibile</Text>
                        </VStack>
                      </Center>
                    )}
                  </Center>
                </Box>

                {/* Product Details */}
                <Box p={{ base: 6, lg: 8 }}>
                  <VStack align="start" spacing={6}>
                    <HStack justify="space-between" width="full" wrap="wrap">
                      <Tag
                        size="lg"
                        bg={accentColor}
                        color="white"
                        borderRadius="full"
                        px={4}
                      >
                        {title}
                      </Tag>
                      <Badge
                        bg="green.600"
                        color="white"
                        fontSize="sm"
                        px={3}
                        py={1}
                      >
                        Disponibile per noleggio
                      </Badge>
                    </HStack>

                    <Heading
                      as="h2"
                      size="lg"
                      color={"grey.100"}
                      lineHeight="1.3"
                    >
                      {selectedItem.title}
                    </Heading>

                    <Divider borderColor={borderColor} />

                    <VStack align="start" spacing={3}>
                      <Text
                        fontWeight="semibold"
                        color={"grey.100"}
                        fontSize="lg"
                      >
                        Specifiche Tecniche
                      </Text>
                      <Box
                        bg="rgba(0, 0, 0, 0.2)"
                        p={4}
                        borderRadius="md"
                        width="full"
                      >
                        <Text color={"grey.100"} lineHeight="1.6">
                          {selectedItem.text}
                        </Text>
                      </Box>
                    </VStack>

                    <Divider borderColor={borderColor} />

                    <VStack align="start" spacing={3} width="full">
                      <Text fontWeight="semibold" color={"grey.100"}>
                        Categorie
                      </Text>
                      <HStack spacing={2} wrap="wrap">
                        {/* <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                          {selectedItem.category}
                        </Badge> */}
                        <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
                          {title}
                        </Badge>
                        <Badge colorScheme="teal" fontSize="sm" px={3} py={1}>
                          Illuminazione
                        </Badge>
                      </HStack>
                    </VStack>

                    <Button
                      as={Link}
                      to={"/contact"}
                      colorScheme="orange"
                      size="lg"
                      width="full"
                      mt={4}
                      bg="linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 20px rgba(255, 140, 0, 0.3)",
                      }}
                      transition="all 0.3s"
                      rightIcon={<ChevronRightIcon />}
                    >
                      <StarIcon mr={2} />
                      Richiedi Informazioni
                    </Button>
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </animated.div>
  );
};

export default Category;
